/**
 * Los candados de Premium.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  UN CANDADO NO ES UN ERROR — Y ESA ES TODA LA DIFERENCIA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sin esto, un cliente de Estándar que entra al historial de costos recibe un
 * error rojo del servidor: *"402 — plan insuficiente"*. Eso no dice "esto es de
 * Premium", dice **"esto se rompió"**. Y quien cree que el sistema falla no sube
 * de plan: escribe a soporte, o se va.
 *
 * Con el candado ve exactamente lo contrario: la función existe, funciona, y
 * está a un clic. Se le enseña **qué se está perdiendo**, no un fallo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTO NO PROTEGE NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Quien decide de verdad es el backend, que devuelve 402 pase lo que pase aquí.
 * Este archivo solo evita que el usuario descubra el límite comiéndose un error.
 * Esconder un botón nunca ha protegido nada.
 */

import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { Boton, cx } from "@/components/ui";
import type { FuncionDelPlan } from "@/lib/types";
import { useAuth } from "@/stores/auth";

/** Las funciones que el backend puede capar. Los nombres vienen de `planes.py`. */
export type FuncionPremium = FuncionDelPlan;

/**
 * ¿El negocio actual tiene esta función?
 *
 * Devuelve `true` mientras no se sepa (la sesión todavía cargando) para no
 * enseñar un candado que después desaparece: un candado que parpadea es peor
 * que ninguno.
 */
export function useTienePremium(funcion: FuncionPremium): boolean {
  const plan = useAuth((e) => e.plan);
  if (!plan) return true;
  return plan.funciones?.[funcion] !== false;
}

/** El plan actual, para enseñarlo. */
export function usePlanActual(): string {
  return useAuth((e) => e.plan?.plan ?? "trial");
}

/**
 * La insignia pequeña que va junto a un título.
 *
 * Se usa cuando la función se ve pero no se puede usar — un enlace, una
 * pestaña. Para bloquear una pantalla entera está `MuroPremium`.
 */
export function InsigniaPremium({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5",
        "text-[11px] font-semibold text-accent",
        className,
      )}
    >
      <Sparkles size={10} />
      Premium
    </span>
  );
}

/**
 * La pantalla que se enseña en lugar del contenido bloqueado.
 *
 * Tres cosas y en este orden, porque es el orden en que se convence a alguien:
 *
 *   1. **Qué hace la función** — concreto, con el beneficio, no el nombre.
 *   2. **Qué le resuelve a él** — el ejemplo real de su negocio.
 *   3. **El botón**.
 *
 * Empezar por el botón es pedir dinero antes de explicar por qué.
 */
export function MuroPremium({
  titulo,
  descripcion,
  ejemplo,
  icono,
}: {
  titulo: string;
  descripcion: string;
  /** El caso concreto. Es lo que convence, no la descripción. */
  ejemplo?: string;
  icono?: React.ReactNode;
}) {
  return (
    <div className="animar-entrada rounded-[var(--radius-card)] border border-accent/30 bg-accent-soft/40 p-6 text-center sm:p-10">
      <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-[var(--radius-card)] bg-accent text-on-accent">
        {icono ?? <Lock size={24} />}
      </div>

      <div className="mb-1 flex justify-center">
        <InsigniaPremium />
      </div>

      <h2 className="text-xl font-semibold text-ink">{titulo}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-soft">
        {descripcion}
      </p>

      {ejemplo && (
        <p className="mx-auto mt-4 max-w-md rounded-[var(--radius-control)] border border-line bg-surface px-4 py-3 text-sm italic text-muted">
          {ejemplo}
        </p>
      )}

      <div className="mt-6">
        <Link to="/premium">
          <Boton>
            <Sparkles size={16} /> Ver qué incluye Premium
          </Boton>
        </Link>
      </div>
    </div>
  );
}

/**
 * Envuelve contenido y lo sustituye por el muro si el plan no lo incluye.
 *
 * Se usa así:
 *
 *     <SoloPremium funcion="analisis" titulo="…" descripcion="…">
 *       <TablaDeMargenes />
 *     </SoloPremium>
 */
export function SoloPremium({
  funcion,
  titulo,
  descripcion,
  ejemplo,
  icono,
  children,
}: {
  funcion: FuncionPremium;
  titulo: string;
  descripcion: string;
  ejemplo?: string;
  icono?: React.ReactNode;
  children: React.ReactNode;
}) {
  const tiene = useTienePremium(funcion);

  if (tiene) return <>{children}</>;

  return (
    <MuroPremium
      titulo={titulo}
      descripcion={descripcion}
      ejemplo={ejemplo}
      icono={icono}
    />
  );
}
